/**
 * Import React libraries.
 */
import * as React from 'react';

/**
 * Import custom components.
 */
import {Size} from '../types';
import {ChipInputContainer, ChipInputField} from './ChipInputField';
import {ChipInputProps, ChipItem} from './ChipInputTypes';


const ChipInput = React.forwardRef<HTMLInputElement, ChipInputProps>(({
                                                                        values,
                                                                        inputId,
                                                                        variants,
                                                                        icon,
                                                                        placeholder,
                                                                        disabled,
                                                                        altPlaceholder,
                                                                        validationMessage,
                                                                        onValueChange,
                                                                        multiLine = true,
                                                                        size = Size.Medium,
                                                                        autoSave = false,
                                                                        ...rest
                                                                      }: ChipInputProps, ref) => {

  const [chips, setChips] = React.useState<string[]>([]);
  const [value, setValue] = React.useState<string>('');
  const [chipItems, setChipItems] = React.useState<ChipItem[]>([]);

  /**
   * Sets initial values for the chips.
   */
  React.useEffect(() => {
    // Let's add initial chip values
    setChips(values);
  }, [values]);

  /**
   * Set chip items for chip input field
   * */
  React.useEffect(() => {
    setChipItems(chips.map((chip, index) => ({
      label: chip,
      icon: icon,
      disabled: false,
      variant: variants && variants[index],
    })));
  }, [chips, variants, icon, size]);


  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (autoSave && value && !event.target?.closest(`${ChipInputContainer}`)?.contains(event.relatedTarget as Node)) {
      setChips([...chips, value]);
      onValueChange([...chips, value]);

      setValue('');
    }
  };

  /**
   * Adds a chip to the chip list.
   * @param event - Event handler from the key down event.
   */
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if ((event.key === 'Enter' || event.key === ' ') && value) {
      event.preventDefault();

      setChips([...chips, value]);
      onValueChange([...chips, value]);

      setValue('');
    }
  };

  /**
   * Removes a specific chip from the chip list.
   * @param index - Index of the chip that needs to be removed.
   */
  const onRemoveChip = (index: number): void => {
    const newChips = [...chips];
    newChips.splice(index, 1);

    setChips(newChips);
    onValueChange(newChips);
  };

  /**
   * Return Chip Input component.
   */
  return (
    <ChipInputField inputId={inputId}
                    ref={ref}
                    items={chipItems}
                    onRemoveItem={(item, index) => onRemoveChip(index)}
                    inputValue={value}
                    onInputValueChange={value => setValue(value)}
                    onInputKeyDown={handleInputKeyDown}
                    placeholder={placeholder}
                    altPlaceholder={altPlaceholder}
                    disabled={disabled}
                    validationMessage={validationMessage}
                    multiLine={multiLine}
                    size={size}
                    onBlur={handleInputBlur}
                    {...rest}
    />
  );
});

export default ChipInput;
